import React, { useState, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import style from "./style.module.scss";
import { formatMessage } from "@local/legacy-utils/i18nHelper";
import { getColorByHighlightType } from "@local/utils";

const FieldSingleSelectSearch = ({ id, label, options, valueField, labelField, small, helperText, multiple, onChangeExtra, readOnly, informationText, input: { value, onChange }, meta, highlightType, showEmptyElement = true }) => {
  // Separate state for controlling the Autocomplete text input
  const [inputText, setInputText] = useState("");

  // Build options list with value as string.
  let computedOptions = [];
  if (multiple) {
    computedOptions = options.map((item) => ({
      ...item,
      [valueField]: item[valueField]?.toString(),
    }));
  } else {
    computedOptions = showEmptyElement
      ? [
          {
            [valueField]: null,
            [labelField]: formatMessage({ id: "cmp.field.select.idx.empty" }),
          },
          ...options.map((item) => ({
            ...item,
            [valueField]: item[valueField]?.toString(),
          })),
        ]
      : options.map((item) => ({
          ...item,
          [valueField]: item[valueField]?.toString(),
        }));
  }

  // Function to extract text from the option label (supports string or JSX)
  const getOptionLabel = (option) => {
    if (typeof option === "string") return option;
    const labelContent = option[labelField];
    if (typeof labelContent === "string") {
      return labelContent;
    }
    if (React.isValidElement(labelContent) && labelContent.props.children) {
      return labelContent.props.children;
    }
    return "";
  };

  // Compute the Autocomplete's selected value.
  let computedValue;
  if (multiple) {
    computedValue = value ? computedOptions.filter((option) => value.includes(option[valueField])) : [];
  } else {
    computedValue = computedOptions.find((option) => option[valueField] === (value !== undefined && value !== null ? value.toString() : null)) || null;
  }

  // When the computedValue changes (e.g. an option is selected), update the input text.
  useEffect(() => {
    if (!multiple && computedValue) {
      setInputText(getOptionLabel(computedValue));
    }
  }, [computedValue, multiple]);

  // Handle selection changes.
  const handleChange = (event, newValue) => {
    if (multiple) {
      const newValues = newValue.map((item) => item[valueField]);
      onChange({ target: { value: newValues } });
      if (onChangeExtra) onChangeExtra({ target: { value: newValues } });
    } else {
      const newVal = newValue ? newValue[valueField] : null;
      onChange(newVal);
      if (onChangeExtra) onChangeExtra({ target: { value: newVal } });
    }
  };

  // Handle changes in the text input (for filtering).
  const handleInputChange = (event, newInputValue) => {
    setInputText(newInputValue);
  };

  // When the Autocomplete loses focus, revert the input text to the selected option's label.
  const handleBlur = () => {
    if (!multiple && computedValue) {
      setInputText(getOptionLabel(computedValue));
    }
  };

  const color = getColorByHighlightType(highlightType);
  return (
    <div className={style.selectWrapper}>
      <FormControl error={(meta?.error ?? "") !== ""} fullWidth variant="outlined" disabled={readOnly}>
        <Autocomplete
          id={id}
          multiple={multiple}
          options={computedOptions}
          getOptionLabel={getOptionLabel}
          value={computedValue}
          onChange={handleChange}
          inputValue={inputText}
          onInputChange={handleInputChange}
          onBlur={handleBlur}
          readOnly={readOnly}
          renderInput={(params) => <TextField {...params} label={label} variant="outlined" size={small ? "small" : "medium"} error={Boolean(meta?.error)} helperText={meta.touched && meta.error ? meta.error : helperText} />}
          renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => <Chip key={option[valueField]} label={getOptionLabel(option)} size={small ? "small" : "medium"} {...getTagProps({ index })} />)}
          sx={{ backgroundColor: `${color}29` }}
        />
        {helperText && !meta?.error ? <FormHelperText id={`select_helper_text_${id}`}>{helperText}</FormHelperText> : null}
        {meta?.error ? (
          <FormHelperText error id={`field-text-helper-text-${id}`}>
            {meta?.error}
          </FormHelperText>
        ) : null}
      </FormControl>
      {informationText && (
        <div className={style.tooltipWrapper}>
          <Tooltip title={informationText}>
            <InfoIcon style={{ fontSize: ".8rem", color: "#555555" }} />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

FieldSingleSelectSearch.propTypes = {};

export default FieldSingleSelectSearch;
